/**
 * Matriz de alçadas e aprovações (ADR 0013, seção 4, e ADR 0021), usadas pela API e pelas telas:
 * tipos de operação, escolha da faixa, validação das faixas e as entradas.
 */
import { z } from 'zod';
import { fromScaled, toScaled } from './money.ts';
import { currencyCode, decimalString, uuid } from './schemas.ts';

export const APPROVAL_TYPES = [
  'purchasing.need',
  'purchasing.contracting',
  'asset.disposal',
  'asset.intercompany_use',
  'asset.term_waiver',
  'finance.payable',
  'it.restricted_software',
  'it.equipment_request',
  'it.software_request',
  'it.invoice',
  'fpa.version',
  'facilities.travel',
  // Política Corporativa de Viagens (0393): internacional e exceção à política, pela matriz.
  'facilities.travel_international',
  'facilities.travel_exception',
  'facilities.travel_advance',
  'facilities.travel_accountability',
  'it.access',
  // Mudança de TI com risco (PJ2): gestor do centro de custo; quem executa não aprova.
  'it.change',
  // Alteração de rateio de contrato (ADR 0094): gestor do centro que recebe o custo.
  'it.contract_allocation',
  // Requisição de vaga (ADR 0091): o recrutamento é na ADP.
  'people.position_request',
] as const;
export type ApprovalType = (typeof APPROVAL_TYPES)[number];

/**
 * Tipos com etapa fixa, sem matriz: a aprovação da necessidade (Compras) e a dispensa de termo de
 * responsabilidade (Patrimônio) e a contratação de software restrito (TI) são sempre do gestor do
 * centro de custo (ou do substituto), qualquer que seja o valor.
 */
export const FIXED_APPROVAL_TYPES: readonly ApprovalType[] = [
  'purchasing.need',
  'asset.term_waiver',
  'it.restricted_software',
  // Pedidos do Service Desk (SD-3): equipamento e software novo, pelo centro de custo de quem usa.
  'it.equipment_request',
  'it.software_request',
  // Atesto da fatura de TI (Fase 3, F7): gestor do centro de custo da fatura (provisório, até a
  // Diretoria cadastrar faixas por valor na matriz).
  'it.invoice',
  // Pedido de acesso a sistema (TI, A2): gestor do centro de custo informado no pedido.
  'it.access',
  // Mudança de TI com risco (PJ2): gestor do centro de custo; quem executa não aprova.
  'it.change',
  // Alteração de rateio de contrato (ADR 0094): gestor do centro que recebe o custo.
  'it.contract_allocation',
];
/** Tipos que seguem a matriz de alçadas. */
export const RULE_APPROVAL_TYPES = APPROVAL_TYPES.filter((t) => !FIXED_APPROVAL_TYPES.includes(t));

/**
 * Quem aprova uma etapa da matriz. Modelo simples (ADR 0080): gestor do centro de custo, diretor da
 * área (gestor do centro de cima) e CEO; nível, perfil e pessoa ficam para as regras antigas.
 */
export const RULE_APPROVER_KINDS = [
  'cost_center_manager',
  'area_director',
  'ceo',
  'level',
  'role',
  'user',
] as const;
export type RuleApproverKind = (typeof RULE_APPROVER_KINDS)[number];
/** No pedido entram também as funções da matriz corporativa (ADR 0101). */
export type RequestApproverKind =
  RuleApproverKind | 'area_manager' | 'head_director' | 'backoffice_head';

/** Em que qualidade a pessoa decidiu (ADR 0050). */
export const DECISION_CAPACITIES = [
  'cost_center_manager',
  'cost_center_substitute',
  'level_holder',
  'level_substitute',
  'role_member',
  'designated_user',
  /** Administrador master (ADR 0051). Só nas decisões antigas: desde o ADR 0052 não é mais usada. */
  'platform_master',
  /** Substituto indicado na ausência de quem aprova (ADR 0074). */
  'absence_delegate',
  /** Modelo simples (ADR 0080). */
  'area_director',
  'area_director_substitute',
  'ceo',
  'ceo_substitute',
  /** Matriz corporativa (ADR 0101). */
  'area_manager',
  'area_manager_substitute',
  'head_director',
  'head_director_substitute',
  'backoffice_head',
  'backoffice_head_substitute',
] as const;
export type DecisionCapacity = (typeof DECISION_CAPACITIES)[number];

export const STEP_MODES = ['any', 'all'] as const;
export type StepMode = (typeof STEP_MODES)[number];

export const APPROVAL_STATUSES = [
  'blocked',
  'pending',
  'approved',
  'rejected',
  'returned',
  'cancelled',
] as const;
export type ApprovalStatus = (typeof APPROVAL_STATUSES)[number];
export type BlockedReason = 'no_rule' | 'no_rate' | 'no_eligible_approver' | 'no_amount';

export const APPROVAL_DECISIONS = ['approve', 'reject', 'return'] as const;
export type ApprovalDecision = (typeof APPROVAL_DECISIONS)[number];

/** Um centavo, na escala de 4 casas do dinheiro: a próxima faixa começa logo depois da anterior. */
const CENT = toScaled('0.01');

// ----- Faixas: sem sobreposição nem lacuna -----

export interface BandLike {
  id?: string;
  minAmount: string;
  maxAmount: string | null;
}

export type BandIssue =
  | { kind: 'overlap'; a: BandLike; b: BandLike }
  | { kind: 'gap'; a: BandLike; b: BandLike; from: string; to: string }
  | { kind: 'open_not_last'; a: BandLike };

/**
 * Confere as faixas ativas de um mesmo tipo, empresa, centro de custo e moeda: ordenadas pelo
 * mínimo, cada uma começa um centavo depois do fim da anterior; só a última pode ficar sem máximo.
 */
export function bandIssues(bands: readonly BandLike[]): BandIssue[] {
  const sorted = [...bands].sort((x, y) =>
    toScaled(x.minAmount) < toScaled(y.minAmount) ? -1 : 1,
  );
  const issues: BandIssue[] = [];
  for (let i = 1; i < sorted.length; i++) {
    const a = sorted[i - 1]!;
    const b = sorted[i]!;
    if (a.maxAmount == null) {
      issues.push({ kind: 'open_not_last', a });
      continue;
    }
    const expected = toScaled(a.maxAmount) + CENT;
    const start = toScaled(b.minAmount);
    if (start < expected) issues.push({ kind: 'overlap', a, b });
    else if (start > expected) {
      issues.push({ kind: 'gap', a, b, from: fromScaled(expected), to: fromScaled(start - CENT) });
    }
  }
  return issues;
}

// ----- Escolha da faixa -----

export interface RuleLike {
  id: string;
  status: string;
  approvalType: string;
  legalEntityId: string;
  costCenterId: string | null;
  currency: string;
  minAmount: string;
  maxAmount: string | null;
  validFrom: string;
  validUntil: string | null;
}

/** A regra vale na data (situação ativa ou já substituída, dentro da vigência). */
export function ruleEffective(r: RuleLike, date: string): boolean {
  return (
    (r.status === 'active' || r.status === 'superseded') &&
    r.validFrom <= date &&
    (r.validUntil == null || r.validUntil >= date)
  );
}

/**
 * Qual faixa vale para o pedido. Vale a regra do centro de custo mais próximo (ele mesmo, depois
 * os superiores, por fim a regra geral da empresa). Nesse escopo, usa a moeda do pedido se houver
 * faixas nela; senão, converte para a moeda das faixas (a cotação usada fica no pedido). Sem
 * cotação de câmbio, nada vale: "no_rate". Nenhuma faixa alcança o valor: "no_rule".
 */
export function matchRule<R extends RuleLike>(
  rules: readonly R[],
  ctx: {
    approvalType: string;
    legalEntityId: string;
    /** Centro de custo do pedido e os superiores, do mais próximo ao mais alto. */
    costCenterChain: readonly string[];
    date: string;
    currency: string;
    /** Valor do pedido na moeda indicada (null = sem cotação de câmbio). */
    amountIn: (currency: string) => string | null;
  },
): { rule: R; currency: string; amount: string } | { rule: null; reason: 'no_rule' | 'no_rate' } {
  const candidates = rules.filter(
    (r) =>
      r.approvalType === ctx.approvalType &&
      r.legalEntityId === ctx.legalEntityId &&
      ruleEffective(r, ctx.date),
  );
  for (const scope of [...ctx.costCenterChain, null]) {
    const inScope = candidates.filter((r) => r.costCenterId === scope);
    if (inScope.length === 0) continue;
    const currencies = [...new Set(inScope.map((r) => r.currency))].sort();
    const currency = currencies.includes(ctx.currency) ? ctx.currency : currencies[0]!;
    const amount = ctx.amountIn(currency);
    if (amount == null) return { rule: null, reason: 'no_rate' };
    const value = toScaled(amount);
    const rule = inScope.find(
      (r) =>
        r.currency === currency &&
        value >= toScaled(r.minAmount) &&
        (r.maxAmount == null || value <= toScaled(r.maxAmount)),
    );
    return rule ? { rule, currency, amount } : { rule: null, reason: 'no_rule' };
  }
  return { rule: null, reason: 'no_rule' };
}

// ----- Entradas -----

export const ruleApproverInput = z
  .object({
    kind: z.enum(RULE_APPROVER_KINDS),
    levelId: uuid.nullable().optional(),
    roleId: uuid.nullable().optional(),
    userId: uuid.nullable().optional(),
  })
  .refine((a) => (a.kind === 'level') === !!a.levelId, {
    message: 'Escolha o nível',
    path: ['levelId'],
  })
  .refine((a) => (a.kind === 'role') === !!a.roleId, {
    message: 'Escolha o perfil de acesso',
    path: ['roleId'],
  })
  .refine((a) => (a.kind === 'user') === !!a.userId, {
    message: 'Escolha a pessoa',
    path: ['userId'],
  });

export const ruleStepInput = z.object({
  mode: z.enum(STEP_MODES).default('any'),
  approvers: z.array(ruleApproverInput).min(1, 'Inclua pelo menos um aprovador').max(10),
});

export const approvalRuleInput = z
  .object({
    approvalType: z.enum(RULE_APPROVAL_TYPES as [ApprovalType, ...ApprovalType[]]),
    legalEntityId: uuid,
    costCenterId: uuid.nullable().optional(),
    currency: currencyCode,
    minAmount: decimalString(15, 4),
    maxAmount: decimalString(15, 4).nullable().optional(),
    name: z.string().trim().min(1).max(200),
    validFrom: z.iso.date(),
    steps: z.array(ruleStepInput).min(1, 'Inclua pelo menos uma etapa').max(10),
  })
  .refine((v) => v.maxAmount == null || toScaled(v.maxAmount) >= toScaled(v.minAmount), {
    message: 'O máximo precisa ser maior ou igual ao mínimo',
    path: ['maxAmount'],
  });
export type ApprovalRuleInput = z.infer<typeof approvalRuleInput>;

/**
 * Faixas iniciais do modelo simples (ADR 0080): dois limites em reais (valores de referência, a
 * ajustar depois). Até o primeiro, o gestor do centro; até o segundo, mais o diretor da área;
 * acima, mais o CEO.
 */
export const initialRulesInput = z
  .object({
    lower: z.number().int().positive().default(5000),
    upper: z.number().int().positive().default(50000),
    /** Só esta empresa (sem ela, todas as empresas ativas). */
    legalEntityId: uuid.nullable().optional(),
  })
  .refine((v) => v.upper > v.lower, {
    message: 'O segundo limite precisa ser maior que o primeiro',
    path: ['upper'],
  });

/**
 * Operações com faixa inicial própria (ADR 0080): em vez das três faixas, uma só, a partir de zero,
 * com estas etapas, uma depois da outra. A versão do orçamento (FP&A) vai com valor zero e é
 * aprovada pelo diretor da área e pelo CEO.
 */
export const INITIAL_SINGLE_BAND: Partial<Record<ApprovalType, RuleApproverKind[]>> = {
  'fpa.version': ['area_director', 'ceo'],
  // Requisição de vaga (ADR 0091, padrão provisório): diretor da área e CEO, sem valor.
  'people.position_request': ['area_director', 'ceo'],
};

export const ruleStatusInput = z.object({
  status: z.literal('inactive'),
  validUntil: z.iso.date().nullable().optional(),
});

export const approvalSimulationInput = z.object({
  approvalType: z.enum(APPROVAL_TYPES),
  legalEntityId: uuid,
  costCenterId: uuid,
  amount: decimalString(15, 4),
  currency: currencyCode,
  date: z.iso.date().optional(),
});

export const approvalDecisionInput = z
  .object({
    decision: z.enum(APPROVAL_DECISIONS),
    /** Head de BackOffice: caso estratégico ou societário, encaminhado ao CEO (ADR 0101). */
    strategic: z.boolean().optional(),
    comment: z
      .string()
      .trim()
      .max(4000)
      .transform((v) => (v === '' ? null : v))
      .nullable()
      .optional(),
  })
  .refine((v) => v.decision === 'approve' || !!v.comment, {
    message: 'Recusar ou devolver exige justificativa',
    path: ['comment'],
  });
// ----- Prazos de aprovação em dias úteis (entrega 4) -----

const DAY_MS = 86_400_000;
const toTime = (d: string) => Date.parse(`${d}T00:00:00Z`);
const toDate = (t: number) => new Date(t).toISOString().slice(0, 10);
const isBusinessDay = (t: number) => {
  const wd = new Date(t).getUTCDay();
  return wd !== 0 && wd !== 6;
};

/** Soma dias úteis (segunda a sexta, sem feriados nesta versão) a uma data ISO. */
export function addBusinessDays(date: string, days: number): string {
  let t = toTime(date);
  let left = days;
  while (left > 0) {
    t += DAY_MS;
    if (isBusinessDay(t)) left -= 1;
  }
  return toDate(t);
}

/** Dias úteis depois de `from` até `to`, inclusive (0 se `to` não passa de `from`). */
export function businessDaysBetween(from: string, to: string): number {
  let count = 0;
  for (let t = toTime(from) + DAY_MS; t <= toTime(to); t += DAY_MS) {
    if (isBusinessDay(t)) count += 1;
  }
  return count;
}

// ----- Prazo com hora no fuso da empresa (ADR 0050, adendo 3) -----
//
// O prazo de N dias úteis começa no instante em que a etapa começa e vence no mesmo horário do
// N-ésimo dia útil seguinte, no fuso da empresa do pedido. Conta-se o tempo útil: só as horas que
// caem de segunda a sexta (sem feriados nesta versão). Começar no fim de semana conta a partir do
// início da segunda-feira. As datas ficam em UTC no banco; o fuso só serve para a conta e a tela.

const HOUR_MS = 3_600_000;

/** Diferença entre o relógio local do fuso e o UTC, em ms, no instante dado. */
function zoneOffset(instant: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(instant));
  const get = (type: string) => Number(parts.find((p) => p.type === type)!.value);
  const wall = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute'),
    get('second'),
  );
  return wall - Math.floor(instant / 1000) * 1000;
}

/** Relógio local (como se fosse UTC) → instante real, no fuso dado. */
function wallToInstant(wall: number, timeZone: string): number {
  let instant = wall - zoneOffset(wall, timeZone);
  instant = wall - zoneOffset(instant, timeZone);
  return instant;
}

const isWeekday = (wall: number) => {
  const wd = new Date(wall).getUTCDay();
  return wd !== 0 && wd !== 6;
};
const startOfWallDay = (wall: number) => Math.floor(wall / DAY_MS) * DAY_MS;

/** Soma horas úteis (segunda a sexta, no fuso da empresa) a um instante. */
export function addBusinessHours(start: Date, hours: number, timeZone: string): Date {
  let wall = start.getTime() + zoneOffset(start.getTime(), timeZone);
  let left = hours * HOUR_MS;
  while (left > 0) {
    if (!isWeekday(wall)) {
      wall = startOfWallDay(wall) + DAY_MS;
      continue;
    }
    const endOfDay = startOfWallDay(wall) + DAY_MS;
    const step = Math.min(left, endOfDay - wall);
    wall += step;
    left -= step;
  }
  return new Date(wallToInstant(wall, timeZone));
}

/** Vencimento de um prazo de N dias úteis contado a partir do início da etapa. */
export function businessDueAt(start: Date, days: number, timeZone: string): Date {
  return addBusinessHours(start, days * 24, timeZone);
}

/** Metade do prazo, em tempo útil: quando sai o lembrete (nunca no fim de semana). */
export function businessHalfwayAt(start: Date, days: number, timeZone: string): Date {
  return addBusinessHours(start, days * 12, timeZone);
}

/**
 * Quantos níveis a etapa já deveria ter subido agora: um a cada prazo inteiro vencido, contado em
 * tempo útil desde o instante em que a etapa começou. Sem prazo, nunca sobe.
 */
export function escalationDepth(
  activatedAt: Date | null,
  slaDays: number | null,
  now: Date,
  timeZone: string,
): number {
  if (!activatedAt || !slaDays) return 0;
  let depth = 0;
  while (depth < 20 && businessDueAt(activatedAt, slaDays * (depth + 1), timeZone) <= now) {
    depth += 1;
  }
  return depth;
}

/** Prazo de aprovação (SLA) por tipo de operação e, opcionalmente, por empresa (entrega 4). */
export const approvalSlaInput = z.object({
  approvalType: z.enum(APPROVAL_TYPES),
  legalEntityId: uuid.nullable().optional(),
  businessDays: z.number().int().min(1).max(60),
  isActive: z.boolean().default(true),
});
export type ApprovalSlaInput = z.infer<typeof approvalSlaInput>;
